'use client'
import React, { FC, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ImageVariants, MediaDTO, MediaType } from '@/core/media/typings'
import { ImageBox } from '@/app/components/common/ImageBox'
import { ScrollDragContainer } from '@/app/[locale]/(public)/components/ScrollDragContainer'
import { Box, Container, Flex, HStack, IconButton, Text } from '@chakra-ui/react'
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons'
import ImageGalleryModal from '@/app/components/modals/ImageGalleryModal'
import { getDisplayImageUrl } from '@/core/media/utils/get-display-image-url'
import {
  CarouselDirection,
  getCarouselSlideIndex
} from '@/app/[locale]/(public)/propiedades/[slug]/_components/media-carousel-navigation'

interface RealEstateMedias {
  medias: MediaDTO[]
}

const RealEstateMedias: FC<RealEstateMedias> = ({ medias }) => {
  const containerRef = useRef<HTMLElement>(null)
  const currentIndexRef = useRef(0)
  const observedWidthRef = useRef<number | null>(null)
  const [currentIndex, setCurrentIndex] = useState(0)

  const filteredMedias = useMemo(() => {
    return medias.filter(
      (media) => media.type === MediaType.Image || media.type === MediaType.Video
    )
  }, [medias])

  const images = useMemo(() => {
    return filteredMedias.filter((media) => media.type === MediaType.Image)
  }, [filteredMedias])

  /**
   * Scrolls the carousel to one exact logical slide.
   *
   * @param targetIndex - Zero-based slide index to display.
   * @param behavior - Browser scrolling behavior.
   * @returns Nothing.
   */
  const scrollToSlide = useCallback(
    (targetIndex: number, behavior: ScrollBehavior = 'smooth'): void => {
      const container = containerRef.current

      if (!container) {
        return
      }

      currentIndexRef.current = targetIndex
      setCurrentIndex(targetIndex)

      if (Math.abs(container.scrollLeft - targetIndex * container.clientWidth) < 1) {
        return
      }

      container.scrollTo({
        left: targetIndex * container.clientWidth,
        behavior
      })
    },
    []
  )

  /**
   * Moves the carousel by at most one logical slide.
   *
   * @param direction - Previous or next direction.
   * @returns Nothing.
   */
  const navigate = useCallback(
    (direction: CarouselDirection): void => {
      if (!filteredMedias.length) {
        return
      }

      const targetIndex = getCarouselSlideIndex(currentIndex, direction, filteredMedias.length)

      scrollToSlide(targetIndex, 'auto')
    },
    [currentIndex, filteredMedias.length, scrollToSlide]
  )

  useEffect(() => {
    const container = containerRef.current

    if (!container || typeof ResizeObserver === 'undefined') {
      return
    }

    /**
     * Keeps the active logical slide exactly aligned after a container resize.
     *
     * @param entries - Resize observations emitted for the carousel container.
     * @returns Nothing.
     */
    const preserveSlideAlignment = (entries: ResizeObserverEntry[]): void => {
      const entry = entries.find(({ target }) => target === container)
      const width = entry?.contentRect.width ?? container.clientWidth
      const previousWidth = observedWidthRef.current

      if (width <= 0) {
        return
      }

      observedWidthRef.current = width

      if (previousWidth === null || Math.abs(previousWidth - width) < 1) {
        return
      }

      const alignedScrollLeft = currentIndexRef.current * width

      if (Math.abs(container.scrollLeft - alignedScrollLeft) < 1) {
        return
      }

      container.scrollTo({
        left: alignedScrollLeft,
        behavior: 'auto'
      })
    }

    const resizeObserver = new ResizeObserver(preserveSlideAlignment)
    resizeObserver.observe(container)

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  /**
   * Records the visible logical slide where a mouse or touch drag begins.
   *
   * Reading the scroll position avoids using stale React state when users
   * chain touch gestures before the scroll-end callback has fired.
   *
   * @returns Nothing.
   */
  const handleDragStart = useCallback((): void => {
    const container = containerRef.current

    if (!container || container.clientWidth <= 0 || !filteredMedias.length) {
      return
    }

    const visibleIndex = Math.min(
      Math.max(Math.round(container.scrollLeft / container.clientWidth), 0),
      filteredMedias.length - 1
    )

    currentIndexRef.current = visibleIndex
    setCurrentIndex(visibleIndex)
  }, [filteredMedias.length])

  /**
   * Keeps a completed drag on the logical slide that is actually visible.
   *
   * This prevents a delayed scroll-end callback from moving a rapid touch
   * sequence back to an earlier slide.
   *
   * @returns Nothing.
   */
  const handleDragEnd = useCallback((): void => {
    const container = containerRef.current

    if (!container || !filteredMedias.length) {
      return
    }

    const visibleIndex = Math.min(
      Math.max(Math.round(container.scrollLeft / container.clientWidth), 0),
      filteredMedias.length - 1
    )

    scrollToSlide(visibleIndex, 'smooth')
  }, [filteredMedias.length, scrollToSlide])

  return (
    <Box position="relative" overflow="hidden" w="full">
      {filteredMedias.length > 1 ? (
        <>
          <IconButton
            aria-label="Contenido multimedia anterior"
            icon={<ChevronLeftIcon boxSize={10} />}
            position="absolute"
            top="50%"
            left="5px"
            transform="translateY(-50%)"
            zIndex="1"
            minW="auto"
            bg="rgba(255,255,255,0.75)"
            isDisabled={currentIndex === 0}
            onClick={() => navigate(-1)}
          />
          <IconButton
            aria-label="Contenido multimedia siguiente"
            icon={<ChevronRightIcon boxSize={10} />}
            position="absolute"
            top="50%"
            right="5px"
            transform="translateY(-50%)"
            zIndex="1"
            minW="auto"
            bg="rgba(255,255,255,0.75)"
            isDisabled={currentIndex === filteredMedias.length - 1}
            onClick={() => navigate(1)}
          />
        </>
      ) : null}
      <ScrollDragContainer
        rounded="md"
        overflowX="auto"
        overflowY="hidden"
        scrollSnapType="x mandatory"
        w="full"
        ref={containerRef}
        mouseScroll={{ inertia: false, rubberBand: false, overscroll: false }}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
        onEndScroll={handleDragEnd}>
        <HStack
          id="scrolled-container"
          alignItems="stretch"
          spacing={0}
          w="full"
          aspectRatio={16 / 9}>
          {filteredMedias.length ? (
            <>
              {filteredMedias.map((media) => {
                if (media.type === MediaType.Image) {
                  const imageIndex = images.findIndex((image) => image.id === media.id)

                  return (
                    <Box
                      key={media.id}
                      w="full"
                      minW="full"
                      h="full"
                      scrollSnapAlign="start"
                      scrollSnapStop="always">
                      <ImageGalleryModal
                        index={imageIndex}
                        medias={images}
                        trigger={
                          <Box
                            as="button"
                            type="button"
                            aria-label={`Abrir galería de imágenes en ${media.name}`}
                            w="full"
                            h="full"
                            border={0}
                            p={0}
                            overflow="hidden"
                            cursor="pointer"
                            _focusVisible={{
                              outline: '2px solid',
                              outlineColor: 'blue.500',
                              outlineOffset: '2px'
                            }}>
                            <ImageBox
                              w="full"
                              h="full"
                              imgProps={{
                                alt: media.name,
                                src: getDisplayImageUrl(media.url, ImageVariants['1920x1080']),
                                fill: true,
                                style: {
                                  objectFit: 'cover'
                                },
                                quality: 100,
                                priority: true
                              }}
                            />
                          </Box>
                        }
                      />
                    </Box>
                  )
                }

                return (
                  <Suspense fallback={null} key={media.id}>
                    <Box
                      key={media.id}
                      minW="full"
                      boxSize="100%"
                      aspectRatio={16 / 9}
                      as={'video'}
                      controls={true}
                      src={new URL(media.url).href}
                      objectPosition="center"
                      bg="black"
                      scrollSnapAlign="start"
                      scrollSnapStop="always"
                      preload="none"
                    />
                  </Suspense>
                )
              })}
            </>
          ) : (
            <Container
              as={Flex}
              alignItems="center"
              justifyContent="center"
              px={4}
              py={2}
              h="full"
              maxW="container.xl">
              <Text fontSize="lg" color="blackAlpha.500">
                Esta propiedad no cuenta con archivos multimedia
              </Text>
            </Container>
          )}
        </HStack>
      </ScrollDragContainer>
    </Box>
  )
}

export default RealEstateMedias
